import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getV1CompaniesCompanyIdJobsJobIdFiles,
  GetV1CompaniesCompanyIdJobsJobIdFilesData,
} from '@treadinc/horizon-api-spec';
import { noop } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import React, { createContext, useCallback } from 'react';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { FileAttachment } from '~hooks/useFileAttachment/models';
import { Job } from '~hooks/useJob/models';
import { makeAllFilesQueryContextKey } from '~pages/Approvals/Files/utils';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

type AllFilesQueryContextType = {
  clearQueryData: () => void;
  data?: FileAttachment[];
  isFetching: boolean;
  refresh: () => void;
};

const AllFilesQueryContext = createContext<AllFilesQueryContextType>({
  clearQueryData: noop,
  isFetching: true,
  refresh: noop,
});

interface AllFilesQueryContextProviderProps {
  job: Job;
}

const AllFilesQueryContextProvider = observer(function AllFilesQueryContextProvider({
  children,
  job,
}: React.PropsWithChildren<AllFilesQueryContextProviderProps>) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const queryKey = makeAllFilesQueryContextKey(job.id, companyId);

  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [queryKey],
    queryFn: async ({ signal }) => {
      // FIXME @todo To be properly paginated in a follow-up PR.
      const query: GetV1CompaniesCompanyIdJobsJobIdFilesData['query'] = {
        'page[limit]': MAX_API_PAGE_SIZE,
      };

      const response = await getV1CompaniesCompanyIdJobsJobIdFiles({
        path: { 'company-id': companyId, 'job-id': job.id },
        query,
        signal,
      });

      const files = response.data.data.map((file) => FileAttachment.parse(file));

      return files;
    },
  });

  const clearQueryData = useCallback(() => {
    queryClient.removeQueries({
      predicate(query) {
        return query.queryKey.some((key) => {
          const keyAsString = String(key);

          return keyAsString.startsWith(queryKey);
        });
      },
    });
  }, [queryKey]);

  return (
    <AllFilesQueryContext.Provider
      value={{
        clearQueryData,
        data: query.data,
        isFetching: query.isFetching && !query.isRefetching,
        refresh: query.refetch,
      }}
    >
      {children}
    </AllFilesQueryContext.Provider>
  );
});

export default AllFilesQueryContext;
export { AllFilesQueryContextProvider };
